// packages/front/src/portfolio/modals/add-account-modal.tsx

import {
  BROKER_SYNC_PROVIDER,
  BROKER_SYNC_PROVIDER_LABELS,
  CURRENCY_LIST,
  type TBrokerSyncProvider,
} from "@signals/shared";
import { useState, type FC, type FormEvent } from "react";
import { Button, Modal, Select } from "../../_common";
import { useCreateAccount } from "../portfolio.hooks";
import { apiErrorMessage } from "../portfolio-api-error";

export interface AddAccountModalProps {
  open: boolean;
  onClose: () => void;
  portfolioId?: string;
}

const TYPES = ["standard", "iis", "ib", "other"];

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const labelClass = "text-sm font-medium";

export const AddAccountModal: FC<AddAccountModalProps> = ({
  open,
  onClose,
  portfolioId,
}) => {
  const createAccount = useCreateAccount();
  const saving = createAccount.isPending;

  const [name, setName] = useState("");
  const [broker, setBroker] = useState("");
  const [type, setType] = useState("standard");
  const [currency, setCurrency] = useState("");
  const [currencyError, setCurrencyError] = useState<string | null>(null);
  const [brokerAccountId, setBrokerAccountId] = useState("");
  const [selectedBrokerSyncProvider, setSelectedBrokerSyncProvider] =
    useState<TBrokerSyncProvider | "">("");
  const [brokerSyncFrom, setBrokerSyncFrom] = useState("");
  const [brokerSyncError, setBrokerSyncError] = useState<string | null>(null);
  // Server rejection of the whole request (for example a broker account already linked elsewhere).
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!currency) {
      setCurrencyError("Select currency");
      return;
    }
    setCurrencyError(null);
    const normalizedBrokerAccountId = brokerAccountId.trim();
    if (selectedBrokerSyncProvider && !normalizedBrokerAccountId) {
      setBrokerSyncError("Enter the broker account ID to enable synchronization");
      return;
    }
    if (selectedBrokerSyncProvider && !brokerSyncFrom) {
      setBrokerSyncError("Select the synchronization start date");
      return;
    }
    setBrokerSyncError(null);
    setSubmitError(null);
    try {
      await createAccount.mutateAsync({
        name,
        broker,
        type,
        currency: currency || "USD",
        ...(normalizedBrokerAccountId
          ? { brokerAccountId: normalizedBrokerAccountId }
          : {}),
        ...(selectedBrokerSyncProvider && brokerSyncFrom
          ? {
              brokerSyncProvider: selectedBrokerSyncProvider,
              brokerSyncFrom,
            }
          : {}),
        ...(portfolioId !== undefined ? { portfolioId } : {}),
      });
      onClose();
    } catch (error) {
      setSubmitError(apiErrorMessage(error, "Could not create the account."));
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Account">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label className={labelClass}>Name</label>
        <input
          className={inputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          aria-label="Name"
        />

        <label className={labelClass}>Broker</label>
        <input
          className={inputClass}
          value={broker}
          onChange={(e) => setBroker(e.target.value)}
          required
          aria-label="Broker"
        />

        <label className={labelClass}>Currency</label>
        <Select
          className={inputClass}
          value={currency}
          onChange={(value) => {
            setCurrency(value);
            setCurrencyError(null);
          }}
          placeholder="Select currency"
          options={CURRENCY_LIST.map((c) => ({ value: c, label: c }))}
          ariaLabel="Currency"
        />
        {currencyError && (
          <p role="alert" className="text-sm text-destructive">
            {currencyError}
          </p>
        )}

        <label className={labelClass}>Type</label>
        <Select
          className={inputClass}
          value={type}
          onChange={setType}
          options={TYPES.map((t) => ({ value: t, label: t }))}
          ariaLabel="Type"
        />

        <label className={labelClass}>Broker Account ID</label>
        <input
          className={inputClass}
          value={brokerAccountId}
          onChange={(e) => {
            setBrokerAccountId(e.target.value);
            setBrokerSyncError(null);
          }}
          aria-label="Broker Account ID"
        />

        <label className={labelClass}>Broker Sync Provider</label>
        <Select
          className={inputClass}
          value={selectedBrokerSyncProvider}
          onChange={(value) => {
            const provider = BROKER_SYNC_PROVIDER.find((candidate) => candidate === value) ?? "";
            setSelectedBrokerSyncProvider(provider);
            if (!provider) setBrokerSyncFrom("");
            setBrokerSyncError(null);
          }}
          placeholder="Disabled"
          options={[
            { value: "", label: "Disabled" },
            ...BROKER_SYNC_PROVIDER.map((provider) => ({
              value: provider,
              label:
                BROKER_SYNC_PROVIDER_LABELS[provider]["en-US"] ?? provider,
            })),
          ]}
          ariaLabel="Broker Sync Provider"
        />

        {selectedBrokerSyncProvider && (
          <>
            <label className={labelClass} htmlFor="broker-sync-from">
              Broker Sync Start Date
            </label>
            <input
              id="broker-sync-from"
              className={inputClass}
              type="date"
              value={brokerSyncFrom}
              onChange={(e) => {
                setBrokerSyncFrom(e.target.value);
                setBrokerSyncError(null);
              }}
              aria-label="Broker Sync Start Date"
            />
          </>
        )}
        {brokerSyncError && (
          <p role="alert" className="text-sm text-destructive">
            {brokerSyncError}
          </p>
        )}
        {submitError && (
          <p role="alert" className="text-sm text-destructive">
            {submitError}
          </p>
        )}

        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
